/**
 * ValuationGate — the entitlement boundary for the Business Valuator view
 * (VALUATOR-UI-1; two-layer check added in REFUSAL-COPY-2).
 *
 * Task-0 decision: the Valuator is a gated VIEW inside the QoE SPA, not a
 * second app. So it reuses `AuthProvider` / `ProtectedRoute` / `AppShell`
 * wholesale, and the only thing that needs to be Valuator-specific is *who is
 * entitled to see it*.
 *
 * Why this and not `<ProtectedRoute allowedRoles={...}>`: `ProtectedRoute`
 * bounces a disallowed role to `/dashboard`. That is the right behaviour for a
 * QoE user who wandered into an admin URL, but it is wrong here in both
 * directions — a `valuation_owner` has no QoE dashboard to be bounced to, and a
 * QoE analyst who lands on `/valuation` deserves to be told they lack the
 * entitlement rather than silently redirected somewhere else. So this renders
 * an honest default-deny panel instead.
 *
 * REFUSAL-COPY-2 (QOE-DEV-BATCH-2 WP-2): this used to check role ALONE and
 * then render entitlement-flavored copy ("licensed separately... granted per
 * user") no matter which layer actually failed — wrong copy whenever the firm
 * held the product but this account's role didn't, or right copy for the
 * wrong reason whenever it was actually a role problem. There are two real
 * locks, the same two `ProductGate` already distinguishes for every other
 * product:
 *   1. Firm entitlement — does the firm hold the `valuation` product at all
 *      (`user.products`). Checked FIRST, through `ProductGate` itself, so
 *      this can never disagree with what the nav already greyed out.
 *   2. Role — is this account's role one of `VALUATION_READ_ROLES`, the
 *      account-level grant within a firm that does hold the product.
 * If firm entitlement fails, `ProductGate`'s own panel renders (the shared
 * refusal component every other product-gated route uses) — and if the role
 * ALSO fails, that panel's `extraReasons` prop appends the role failure in
 * its own vocabulary, so a CPA who fixes one lock and comes back doesn't hit
 * the other one unannounced. If only the role fails, `ProductGate` passes
 * `children` through (the firm layer is satisfied) and this component's own
 * role-refusal panel renders in its place, instead of the real page.
 *
 * This is a UX gate, not a security boundary. The server is the authority:
 * every valuation route is gated on `VALUATION_READ_ROLES` in
 * `apps/api/authz.py` and returns 403 `VALUATION_ROLE_REQUIRED` regardless of
 * what this component decides to render.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { ProductGate } from '@/components/ProductGate'
import { VALUATION_READ_ROLES } from '@/types/valuation'

const VALUATOR_LABEL = 'Business Valuation'

function RoleDeniedPanel({ role, email }: { role: string | undefined; email: string | undefined }) {
  return (
    <div className="mx-auto max-w-xl p-6">
      <div className="rounded-xl border border-pal-line bg-pal-white p-6">
        <h1 className="text-lg font-semibold text-pal-ink">{VALUATOR_LABEL}</h1>
        <p className="mt-2 text-sm text-pal-ink-soft">
          Your firm has the {VALUATOR_LABEL}, but this account's role ({role ?? 'unknown'}) is not
          one of the roles granted access to it ({VALUATION_READ_ROLES.join(', ')}).
        </p>
        <dl className="mt-4 space-y-1 text-xs text-gray-500">
          <div className="flex gap-2">
            <dt>Signed in as</dt>
            <dd className="font-medium text-gray-700">{email ?? '—'}</dd>
          </div>
          <div className="flex gap-2">
            <dt>Role</dt>
            <dd className="font-mono text-gray-700">{role ?? '—'}</dd>
          </div>
        </dl>
        <p className="mt-4 text-sm text-gray-600">
          Ask your firm administrator to change this account's role to one of the roles above.
        </p>
        <Link
          to="/dashboard"
          className="mt-5 inline-block text-sm font-medium text-pal-navy underline"
        >
          Back to dashboard
        </Link>
      </div>
    </div>
  )
}

export function ValuationGate({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const role = user?.role as string | undefined
  const roleEntitled = !!role && (VALUATION_READ_ROLES as readonly string[]).includes(role)

  const roleFailureReason = !roleEntitled
    ? `Separately, this account's role (${role ?? 'unknown'}) is not one of the roles ` +
      `granted access to the ${VALUATOR_LABEL} (${VALUATION_READ_ROLES.join(', ')}).`
    : undefined

  return (
    <ProductGate
      product="valuation"
      label={VALUATOR_LABEL}
      extraReasons={roleFailureReason ? [roleFailureReason] : undefined}
    >
      {roleEntitled ? children : <RoleDeniedPanel role={role} email={user?.email} />}
    </ProductGate>
  )
}
